/**
 * The view is a user preference, not a build-time feature flag.
 *
 * URL overrides are deliberately session-only so support can reproduce a view
 * without changing the user's stored preference.
 */
export const VIEW_MODES = Object.freeze({
    LEGACY: 'legacy',
    MODERN: 'modern',
});

export const VIEW_MODE_STORAGE_KEY = 'maximal.viewMode';

export const isViewMode = (value) => value === VIEW_MODES.LEGACY || value === VIEW_MODES.MODERN;

export const readUrlOverride = (search) => {
    const source = search ?? (typeof window !== 'undefined' ? window.location.search : '');
    const value = new URLSearchParams(source).get('view');
    return isViewMode(value) ? value : null;
};

const readStoredViewMode = () => {
    try {
        const value = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        return isViewMode(value) ? value : null;
    } catch {
        return null;
    }
};

const tenantDefault = (tenantContext) => (
    tenantContext?.viewMode
    || tenantContext?.brand?.viewMode
    || tenantContext?.brand?.defaultViewMode
    || tenantContext?.features?.viewMode
);

/** Resolve the view without reading hostnames or tenant-specific globals. */
export const resolveViewMode = ({ search, stored, user, tenantContext } = {}) => {
    const urlMode = readUrlOverride(search);
    if (urlMode) return urlMode;

    const storedMode = isViewMode(stored) ? stored : readStoredViewMode();
    if (storedMode) return storedMode;

    const userMode = user?.preferences?.viewMode;
    if (isViewMode(userMode)) return userMode;

    const defaultMode = tenantDefault(tenantContext);
    return isViewMode(defaultMode) ? defaultMode : VIEW_MODES.LEGACY;
};

export const setViewMode = (mode) => {
    if (!isViewMode(mode)) throw new Error(`Unsupported view mode: ${mode}`);
    try {
        window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
        // Storage is a convenience. The provider still updates in memory.
    }
    return mode;
};

